// material から Box, Grid をインポート
import { Box, Grid } from '@mui/material';
// ag-grid-community から GetContextMenuItems, GridApi, RowDoubleClickedEvent をインポート
import { GetContextMenuItems, GridApi, RowDoubleClickedEvent } from 'ag-grid-community';
// ag-grid-community から useEffect, useRef, useState をインポート
import { useEffect, useRef, useState } from 'react';
// react-i18next から useTranslation をインポート
import { useTranslation } from 'react-i18next';
// react-router-dom から useNavigate, useParams をインポート
import { useNavigate, useParams } from 'react-router-dom';
// useEnqueteStatusPageApi から RequestedTargetEntity, useEnqueteStatusPageApi をインポート
import { RequestedTargetEntity, useEnqueteStatusPageApi } from 'src/api/useEnqueteStatusPageApi';
// LoadableComponent から LoadableComponent をインポート
import { LoadableComponent } from 'src/components/app-components/bonsai/LoadableComponent';
// StyledAgGrid から StyledAgGrid をインポート
import { StyledAgGrid } from 'src/components/app-components/StyledAgGrid';
// ENQUETE_ANSWER_PATH をインポート
import { ENQUETE_ANSWER_PATH } from 'src/features/general/enquete-answer/routes/path';
// useEnqueteStatusList をインポート
import { useEnqueteStatusList } from 'src/features/general/enquete-status/hooks/useEnqueteStatusList';
// useSettings をインポート
import useSettings from 'src/hooks/useSettings';
// store から useAppSelector, useDispatch をインポート
import { useAppSelector, useDispatch } from 'src/redux/store';
// enqueteStatusSlice から setFilteredData をインポート
import { setFilteredData } from '../../../../../store/enqueteStatusSlice';
// useAction5RequestTargetDataGridColDef をインポート
import useAction5RequestTargetDataGridColDef from '../hooks/useAction5RequestTargetDataGridColDef';

export const Action5RequestTargetDataGrid: React.FC = () => {
  // respondentRequestId取得
  const { id: respondentRequestId } = useParams();
  // パラメータの取得
  const params = new URLSearchParams(window.location.search);
  // パラメータの取得からsurveyDetailIdを取得
  const surveyDetailId: string = String(params.get('surveyDetailId'));
  // themeModeの取得
  const { themeMode } = useSettings();
  // 現在のテーマモードの判定
  const isLight = themeMode === 'light';
  // 翻訳関数を取得
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { defaultColDef, columnDefs } = useAction5RequestTargetDataGridColDef();
  const gridApiRef = useRef<GridApi | null>(null);
  // Api: getRequestedTargetListを取得
  const { getRequestedTargetList } = useEnqueteStatusPageApi();
  const { setIsLoading } = useEnqueteStatusList();

  const isLoading = useAppSelector((state) => state.enqueteStatus.isLoading);
  const updateCounter = useAppSelector((state) => state.enqueteStatus.updateCounter);
  // テーブルデータ
  const [rowData, setRowData] = useState<RequestedTargetEntity[]>([]);

  const refreshRowData = async () => {
    setIsLoading(true);

    // todo: error handling されていないため、おそらく error が throw されるとローディング状態のままになると思われる
    if (respondentRequestId) {
      // データ取得
      const data = await getRequestedTargetList(respondentRequestId);
      if (data) {
        // フィルター条件のクリア
        gridApiRef.current?.setFilterModel(null);
        // テーブルデータを設定する
        setRowData(data);
        dispatch(setFilteredData(data));
      }
    }

    setIsLoading(false);
  };
  // 行ダブルクリック時のコールバック
  const onRowDoubleClickedCallback = (event: RowDoubleClickedEvent) => {
    // 該当する回答へ遷移する
    navigate(
      `${ENQUETE_ANSWER_PATH.INPUT}${event.data.respondentTargetId}?respondentRequestId=${respondentRequestId}&surveyDetailId=${surveyDetailId}`,
    );
  };

  // 行右クリック時のコールバック
  const contextMenuCallback: GetContextMenuItems = (params) => {
    // データ未選択時の処理
    if (params.node === null) return [];

    const menuItems = [
      {
        name: t('enqueteStatus.contextMenu.goToSurveyAnswerInput'),
        action: () => {
          // 該当する回答へ遷移する
          navigate(
            `${ENQUETE_ANSWER_PATH.INPUT}${params.node?.data.respondentTargetId}?respondentRequestId=${respondentRequestId}&surveyDetailId=${surveyDetailId}`,
          );
        },
      },
    ];

    return menuItems;
  };

  useEffect(() => {
    // データを再取得する
    refreshRowData();
  }, [respondentRequestId, updateCounter]); //eslint-disable-line

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box
          className={isLight ? 'ag-theme-alpine' : 'ag-theme-alpine-dark'}
          sx={{
            height: '80vh',
            width: '100%',
          }}>
          <LoadableComponent isLoading={isLoading}>
            <StyledAgGrid
              defaultColDef={defaultColDef}
              coldef={columnDefs}
              rows={rowData}
              contextMenu={contextMenuCallback}
              onRowDoubleClickedCallback={onRowDoubleClickedCallback}
              suppressAutoSize={true}
              storageKey={'RequestTargetDataTable'}
              onGridReady={(params) => {
                gridApiRef.current = params.api;
              }}
              onFilterChangedCallback={(event) => {
                //表のフィルター条件を保存する
                const filteredData: RequestedTargetEntity[] = [];
                event.api.forEachNodeAfterFilter((node) => {
                  if (node.data) {
                    filteredData.push(node.data);
                  }
                });
                dispatch(setFilteredData(filteredData));
              }}
            />
          </LoadableComponent>
        </Box>
      </Grid>
    </Grid>
  );
};
